import { db, storage } from './config';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  setDoc,
  onSnapshot,
  type Unsubscribe,
} from 'firebase/firestore';
import {
  ref as storageRef,
  uploadBytes,
  uploadBytesResumable,
  getDownloadURL,
  deleteObject,
} from 'firebase/storage';

export interface ClinicalTemplate {
  id?: string;
  type:
    | 'PRESCRIPTION'
    | 'EXAM'
    | 'CERTIFICATE'
    | 'SURGERY'
    | 'REFERRAL'
    | 'LENS_ORDER_EMAIL'
    | 'PDF_FORM';
  title: string;
  content: string; // Markdown or HTML (corpo do email quando LENS_ORDER_EMAIL). Para PDF_FORM, texto descritivo/instruções (pode ficar vazio).
  fields?: TemplateField[];

  /**
   * Módulos de exame do atendimento que este modelo de laudo espera
   * (slugs de `encounterCatalog.ts`). Usado por modelos `CERTIFICATE` de
   * laudo cujo `content` traz tokens `{{ex:...}}` resolvidos a partir do
   * painel "Exames do atendimento" (ver `src/utils/examTags.ts`). Opcional
   * e retrocompatível — modelos sem exame simplesmente não têm.
   */
  examModuleIds?: string[];
  /**
   * Campos extras usados APENAS quando `type === 'LENS_ORDER_EMAIL'`.
   * Todos são strings que passam pelo mesmo engine de substituição de
   * placeholders (`{{paciente}}`, `{{od}}`, etc.) antes de virar um
   * `mailto:` link. Opcionais porque o médico pode querer deixar em
   * branco e preencher na hora do envio.
   */
  emailSubject?: string;
  emailTo?: string;
  emailCc?: string;

  /**
   * Campos APENAS para `type === 'PDF_FORM'`. O PDF em branco fica no
   * Firebase Storage; o Firestore guarda apenas o endereço e a lista de
   * campos AcroForm detectados + mapeamento opcional pra dados do paciente/
   * profissional. Na hora do uso, o app baixa o PDF, preenche com pdf-lib
   * e devolve um novo arquivo pro médico.
   */
  pdfUrl?: string;
  pdfPath?: string; // caminho no Storage — usado na hora de deletar
  pdfFileName?: string; // nome original do arquivo (pra UX)
  pdfFields?: PdfFormFieldDef[];
}

/**
 * Rótulo de cada tipo, do jeito que o médico vê em "Modelos e Protocolos
 * Clínicos". Fonte única pra qualquer seletor que agrupe modelos por tipo.
 */
export const TEMPLATE_TYPE_LABELS: Record<ClinicalTemplate['type'], string> = {
  CERTIFICATE: 'Atestados / Laudos / Declarações',
  REFERRAL: 'Encaminhamentos',
  EXAM: 'Evoluções / Consultas',
  SURGERY: 'Cirurgias',
  PRESCRIPTION: 'Receitas',
  LENS_ORDER_EMAIL: 'Email — Pedido de lente',
  PDF_FORM: 'PDF editável',
};

export interface TemplateField {
  key: string;
  label: string;
  type: 'text' | 'number' | 'select' | 'textarea' | 'date';
  options?: string[]; // For select type
  required?: boolean;
  defaultValue?: string;
}

/** Origem de auto-preenchimento de um campo de PDF. 'none' = médico digita
 *  na hora; os outros são substituídos automaticamente a partir do paciente
 *  selecionado, do profissional logado, ou da data corrente. */
export type PdfMapping =
  | 'none'
  | 'patient.name'
  | 'patient.cpf'
  | 'patient.rg'
  | 'patient.birthDate'
  | 'patient.age'
  | 'patient.phone'
  | 'patient.address'
  | 'patient.city'
  | 'patient.cns'
  | 'patient.motherName'
  | 'patient.guardianName'
  | 'patient.lastCid'
  | 'patient.lastDiagnoses'
  | 'professional.name'
  | 'professional.crm'
  | 'professional.cns'
  | 'clinic.name'
  | 'clinic.cnes'
  | 'clinic.address'
  // Estabelecimento de saúde — cadastrados em Configurações →
  // Estabelecimento de Saúde (sub-doc settings/establishment). Canônicos
  // pro cabeçalho dos laudos SUS; quando vazios, caem em `clinic.*`.
  | 'establishment.name'
  | 'establishment.cnes'
  | 'establishment.cnpj'
  | 'establishment.secretaria'
  | 'establishment.municipio'
  | 'establishment.uf'
  | 'establishment.codIbge'
  | 'establishment.endereco'
  | 'establishment.telefone'
  // Dados clínicos da última consulta — derivados dos módulos de exame do
  // atendimento (ver src/services/pdf/clinicalContext.ts).
  | 'clinical.pioOd'
  | 'clinical.pioOe'
  | 'clinical.paquimetriaOd'
  | 'clinical.paquimetriaOe'
  | 'clinical.escavacaoOd'
  | 'clinical.escavacaoOe'
  | 'clinical.glaucomaClassification'
  | 'clinical.staging'
  // Do histórico (utils/historyExams.ts): PIO basal + 1ª consulta com o médico.
  | 'clinical.pioBasalOd'
  | 'clinical.pioBasalOe'
  | 'clinical.pioBasalDate'
  | 'clinical.firstConsultDate'
  | 'clinical.cid'
  | 'clinical.diagnosis'
  // Medicamentos de glaucoma do CEAF resolvidos da prescrição da consulta
  // (ver src/data/ceafGlaucoma.ts). `.ceaf` = texto EXATO pro dropdown
  // "Selecao med N" do LME; `.qty` = quantidade mensal (repetida nas 6
  // colunas de mês daquela linha). Associações fixas já vêm quebradas em
  // monofármacos; os índices seguem a ordem da prescrição, deduplicados.
  | 'medication.1.ceaf'
  | 'medication.1.qty'
  | 'medication.2.ceaf'
  | 'medication.2.qty'
  | 'medication.3.ceaf'
  | 'medication.3.qty'
  | 'medication.4.ceaf'
  | 'medication.4.qty'
  | 'medication.5.ceaf'
  | 'medication.5.qty'
  | 'medication.6.ceaf'
  | 'medication.6.qty'
  | 'date.today';

export interface PdfFormFieldDef {
  /** Nome interno do campo AcroForm — é a chave usada por pdf-lib pra
   *  preencher. Não tente renomear, é o que o PDF espera. */
  name: string;
  /** Rótulo amigável mostrado ao médico na hora de preencher. */
  label: string;
  /** Tipo de widget detectado no PDF. */
  type: 'text' | 'checkbox' | 'radio' | 'dropdown' | 'optionlist';
  /** Opções (para radio/dropdown/optionlist). */
  options?: string[];
  /** Mapeamento pra auto-preenchimento. `undefined` ou `'none'` = manual. */
  mapping?: PdfMapping;
  /** Valor padrão (texto livre). */
  defaultValue?: string;
}

/**
 * Modelos CERTIFICATE de laudo cujo corpo puxa valores MEDIDOS do painel
 * "Exames do atendimento" via tokens {{ex:<modulo>}} (resolvidos por
 * `src/utils/examTags.ts` ao carregar o modelo / clicar em "Preencher
 * exames"). `examModuleIds` documenta os módulos esperados. Reusado pelo seed
 * de clínica nova (`seedDefaultTemplates`) e pelo backfill de clínica
 * existente (`seedExamLaudosIfMissing`).
 */
const EXAM_LAUDO_SEEDS: Array<Omit<ClinicalTemplate, 'id'> & { seedId: string }> = [
  {
    seedId: 'laudo-glaucoma-ceaf',
    type: 'CERTIFICATE',
    title: 'Laudo de Glaucoma (CEAF)',
    examModuleIds: [
      'avlcc',
      'pio',
      'paquimetria',
      'biomicroscopia',
      'fundoscopia',
      'relacao-escavacao-disco',
      'campo-visual',
      'classificacao-glaucoma',
      'estadiamento-glaucoma',
      'cid',
    ],
    content: `LAUDO MÉDICO OFTALMOLÓGICO — GLAUCOMA

Paciente: {{paciente}}
CPF: {{cpf}}   Idade: {{idade}}
Data: {{data}}

Atesto, para fins de solicitação ao Componente Especializado da Assistência Farmacêutica (CEAF), que o(a) paciente acima encontra-se em acompanhamento oftalmológico com diagnóstico de glaucoma, apresentando os seguintes achados ao exame realizado nesta data:

Acuidade visual com correção (AVLCC): {{ex:avlcc}}
Pressão intraocular (PIO): {{ex:pio}}
Paquimetria corneana: {{ex:paquimetria}}
Relação escavação/disco (E/D): {{ex:relacao-escavacao-disco}}
Campo visual: {{ex:campo-visual}}
Biomicroscopia: {{ex:biomicroscopia}}
Fundoscopia: {{ex:fundoscopia}}

Classificação: {{ex:classificacao-glaucoma}}
Estadiamento: {{ex:estadiamento-glaucoma}}
CID-10: {{ex:cid}}

Diante do quadro de glaucoma com necessidade de controle pressórico contínuo, justifica-se a manutenção do tratamento medicamentoso de uso prolongado, sob acompanhamento oftalmológico periódico, sendo o(a) paciente elegível ao fornecimento da medicação pelo CEAF.

{{data}}.


____________________________________
{{medico}}
CRM: {{crm}}`,
  },
  {
    seedId: 'laudo-aptidao-cnh',
    type: 'CERTIFICATE',
    title: 'Laudo de Aptidão Visual (CNH/concurso)',
    examModuleIds: [
      'avlsc',
      'avlcc',
      'avpcc',
      'visao-cores',
      'campo-visual',
      'biomicroscopia',
      'pio',
    ],
    content: `LAUDO DE APTIDÃO VISUAL

Paciente: {{paciente}}
CPF: {{cpf}}   Data de nascimento: {{data_nascimento}}
Data do exame: {{data}}

Declaro que o(a) paciente acima foi submetido(a) a avaliação oftalmológica nesta data, apresentando:

Acuidade visual p/ longe sem correção (AVLSC): {{ex:avlsc}}
Acuidade visual p/ longe com correção (AVLCC): {{ex:avlcc}}
Acuidade visual p/ perto com correção (AVPCC): {{ex:avpcc}}
Visão de cores (Ishihara): {{ex:visao-cores}}
Campo visual (confrontação): {{ex:campo-visual}}
Pressão intraocular: {{ex:pio}}
Biomicroscopia: {{ex:biomicroscopia}}

PARECER: Do ponto de vista oftalmológico, o(a) paciente encontra-se APTO(A) / INAPTO(A) para __________________________ (ex.: condução de veículos / concurso / porte de arma).

Observações: ________________________________________

{{data}}.


____________________________________
{{medico}}
CRM: {{crm}}`,
  },
  {
    seedId: 'laudo-baixa-visao',
    type: 'CERTIFICATE',
    title: 'Laudo de Baixa Visão / Benefício',
    examModuleIds: [
      'avlsc',
      'avlcc',
      'campo-visual',
      'biomicroscopia',
      'fundoscopia',
      'cid',
      'diagnostico',
    ],
    content: `LAUDO OFTALMOLÓGICO — BAIXA VISÃO

Paciente: {{paciente}}
CPF: {{cpf}}   Idade: {{idade}}
Data: {{data}}

Atesto, para os devidos fins (benefício / isenção / perícia), que o(a) paciente acima apresenta déficit visual conforme avaliação oftalmológica realizada nesta data:

Acuidade visual sem correção (AVLSC): {{ex:avlsc}}
Acuidade visual com correção (AVLCC): {{ex:avlcc}}
Campo visual: {{ex:campo-visual}}
Biomicroscopia: {{ex:biomicroscopia}}
Fundoscopia: {{ex:fundoscopia}}

Diagnóstico: {{ex:diagnostico}}
CID-10: {{ex:cid}}

Conclusão: Paciente com baixa acuidade visual e/ou alteração de campo visual que persiste mesmo com a melhor correção óptica, caracterizando deficiência visual para os fins a que se destina este laudo.

{{data}}.


____________________________________
{{medico}}
CRM: {{crm}}`,
  },
  {
    seedId: 'laudo-preop-catarata',
    type: 'CERTIFICATE',
    title: 'Laudo Pré-operatório de Catarata',
    examModuleIds: [
      'avlsc',
      'avlcc',
      'biometria',
      'ceratometria',
      'biomicroscopia',
      'pio',
      'fundoscopia',
    ],
    content: `LAUDO PRÉ-OPERATÓRIO DE CATARATA

Paciente: {{paciente}}
CPF: {{cpf}}   Idade: {{idade}}
Data: {{data}}

Avaliação oftalmológica pré-operatória do(a) paciente acima, com indicação de cirurgia de catarata por facoemulsificação com implante de lente intraocular:

Acuidade visual sem correção (AVLSC): {{ex:avlsc}}
Acuidade visual com correção (AVLCC): {{ex:avlcc}}
Pressão intraocular: {{ex:pio}}
Biomicroscopia: {{ex:biomicroscopia}}
Fundoscopia: {{ex:fundoscopia}}

BIOMETRIA
  Comprimento axial (AL): OD {{ex:biometria:al:OD}} | OE {{ex:biometria:al:OE}}
  Ceratometria: {{ex:ceratometria}}
  LIO alvo: OD {{ex:biometria:lio_alvo:OD}} | OE {{ex:biometria:lio_alvo:OE}}
  Modelo de LIO: {{ex:biometria:lio_modelo}}

Conclusão: Paciente com catarata e indicação cirúrgica, apto(a) ao procedimento conforme avaliação acima.

{{data}}.


____________________________________
{{medico}}
CRM: {{crm}}`,
  },
];

export const TemplateService = {
  async getTemplates(clinicId: string): Promise<ClinicalTemplate[]> {
    const colRef = collection(db, 'clinics', clinicId, 'templates');
    const snapshot = await getDocs(colRef);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as ClinicalTemplate));
  },

  /**
   * Assina a coleção de modelos em tempo real e devolve o unsubscribe.
   * Existe porque as telas que listam modelos ficam montadas por horas
   * (aba de atendimento aberta o dia inteiro) e o `getDocs` de mount
   * deixava modelo recém-criado em /modelos invisível até dar F5.
   *
   * Sem `orderBy`: a ordenação é por título com `localeCompare('pt-BR')`
   * em cada consumidor, o que o Firestore não faz.
   */
  subscribeTemplates(
    clinicId: string,
    onData: (list: ClinicalTemplate[]) => void,
    onError?: (err: Error) => void,
  ): Unsubscribe {
    const colRef = collection(db, 'clinics', clinicId, 'templates');
    return onSnapshot(
      colRef,
      (snapshot) => {
        onData(snapshot.docs.map(d => ({ id: d.id, ...d.data() } as ClinicalTemplate)));
      },
      (err) => {
        console.error('[templates] falha na assinatura de modelos:', err);
        onError?.(err);
      },
    );
  },

  async addTemplate(clinicId: string, template: Omit<ClinicalTemplate, 'id'>): Promise<string> {
    const colRef = collection(db, 'clinics', clinicId, 'templates');
    const docRef = await addDoc(colRef, template);
    return docRef.id;
  },

  async updateTemplate(clinicId: string, id: string, data: Partial<ClinicalTemplate>): Promise<void> {
    const docRef = doc(db, 'clinics', clinicId, 'templates', id);
    await updateDoc(docRef, data);
  },

  async deleteTemplate(clinicId: string, id: string): Promise<void> {
    // Se o template tinha um PDF no Storage, tenta remover antes de
    // apagar o doc. Best-effort: se o arquivo já sumiu, seguimos com a
    // exclusão do Firestore — deixar um doc órfão apontando pra um PDF
    // inexistente é pior do que deixar o PDF órfão no Storage.
    try {
      const list = await this.getTemplates(clinicId);
      const target = list.find((t) => t.id === id);
      if (target?.pdfPath) {
        try {
          await deleteObject(storageRef(storage, target.pdfPath));
        } catch {
          // arquivo pode já ter sumido — não bloqueia a exclusão
        }
      }
    } catch {
      // Se nem consegui listar, pelo menos apago o doc abaixo.
    }
    const docRef = doc(db, 'clinics', clinicId, 'templates', id);
    await deleteDoc(docRef);
  },

  /**
   * Sobe um PDF-modelo em branco pro Storage e devolve URL + caminho.
   * O caminho é devolvido separadamente porque o download URL é opaco
   * (embute um token) — sem o path não dá pra deletar depois.
   *
   * Caminho: clinics/{clinicId}/pdf-templates/{timestamp}_{safeName}
   */
  /**
   * Sobe o PDF e devolve URL + path. Quando `onProgress` é fornecido,
   * usa `uploadBytesResumable` e reporta percentual + bytes em tempo
   * real — essencial pra PDFs grandes (passe livre, laudo SUS) onde o
   * upload pode levar dezenas de segundos e o usuário precisa ver que
   * a operação não travou.
   */
  async uploadPdfTemplateFile(
    clinicId: string,
    file: File,
    onProgress?: (info: { bytesTransferred: number; totalBytes: number; percent: number }) => void,
  ): Promise<{ url: string; path: string }> {
    const timestamp = Date.now();
    const safeName = `${timestamp}_${file.name.replace(/[^a-zA-Z0-9._-]/g, '_')}`;
    const path = `clinics/${clinicId}/pdf-templates/${safeName}`;
    const fileRef = storageRef(storage, path);
    const metadata = { contentType: file.type || 'application/pdf' };

    if (!onProgress) {
      // Sem callback de progresso, mantém o caminho simples antigo.
      await uploadBytes(fileRef, file, metadata);
    } else {
      await new Promise<void>((resolve, reject) => {
        const task = uploadBytesResumable(fileRef, file, metadata);
        task.on(
          'state_changed',
          (snap) => {
            const total = snap.totalBytes || file.size || 1;
            onProgress({
              bytesTransferred: snap.bytesTransferred,
              totalBytes: total,
              percent: Math.round((snap.bytesTransferred / total) * 100),
            });
          },
          (err) => reject(err),
          () => resolve(),
        );
      });
    }
    const url = await getDownloadURL(fileRef);
    return { url, path };
  },

  // T214: Seed default ophthalmology templates
  async seedDefaultTemplates(clinicId: string): Promise<void> {
    const defaults: (Omit<ClinicalTemplate, 'id'> & { seedId: string })[] = [
      {
        seedId: 'consulta-rotina',
        type: 'EXAM',
        title: 'Consulta de rotina',
        content: `CONSULTA OFTALMOLÓGICA DE ROTINA

Paciente: [NOME_PACIENTE]
Data: [DATA]

1. ANAMNESE
   Queixa principal: [QUEIXA]
   HMA: [HMA]
   Antecedentes oculares: [ANTECEDENTES]
   Medicações em uso: [MEDICACOES]

2. ACUIDADE VISUAL
   OD: [AV_OD] (cc/sc)
   OE: [AV_OE] (cc/sc)

3. REFRAÇÃO
   OD: [REF_OD]
   OE: [REF_OE]

4. BIOMICROSCOPIA
   OD: [BIO_OD]
   OE: [BIO_OE]

5. TONOMETRIA
   OD: [PIO_OD] mmHg
   OE: [PIO_OE] mmHg

6. FUNDOSCOPIA
   OD: [FUNDO_OD]
   OE: [FUNDO_OE]

DIAGNÓSTICO: [DIAGNOSTICO]
CONDUTA: [CONDUTA]

____________________________
[NOME_MEDICO]
CRM [CRM] - RQE [RQE]`,
        fields: [
          { key: 'queixa', label: 'Queixa Principal', type: 'textarea', required: true },
          { key: 'av_od', label: 'AV OD', type: 'text', required: true },
          { key: 'av_oe', label: 'AV OE', type: 'text', required: true },
          { key: 'ref_od', label: 'Refração OD', type: 'text' },
          { key: 'ref_oe', label: 'Refração OE', type: 'text' },
          { key: 'pio_od', label: 'PIO OD (mmHg)', type: 'number', required: true },
          { key: 'pio_oe', label: 'PIO OE (mmHg)', type: 'number', required: true },
          { key: 'diagnostico', label: 'Diagnóstico', type: 'textarea', required: true },
          { key: 'conduta', label: 'Conduta', type: 'textarea', required: true },
        ],
      },
      {
        seedId: 'avaliacao-catarata',
        type: 'SURGERY',
        title: 'Avaliação de catarata',
        content: `AVALIAÇÃO PRÉ-OPERATÓRIA DE CATARATA

Paciente: [NOME_PACIENTE]
Data: [DATA]

1. ACUIDADE VISUAL
   OD: [AV_OD] (cc/sc)
   OE: [AV_OE] (cc/sc)

2. REFRAÇÃO MANIFESTA
   OD: [REF_OD]
   OE: [REF_OE]

3. BIOMICROSCOPIA
   OD: Catarata [TIPO_CATARATA_OD] - Grau [GRAU_OD]
   OE: Catarata [TIPO_CATARATA_OE] - Grau [GRAU_OE]

4. BIOMETRIA
   OD: Axial [AXIAL_OD] mm | K1: [K1_OD] | K2: [K2_OD]
   OE: Axial [AXIAL_OE] mm | K1: [K1_OE] | K2: [K2_OE]

5. LIO CALCULADA
   OD: [LIO_OD] D ([FORMULA])
   OE: [LIO_OE] D ([FORMULA])

6. MICROSCOPIA ESPECULAR
   OD: [ME_OD] céls/mm²
   OE: [ME_OE] céls/mm²

INDICAÇÃO CIRÚRGICA: [INDICACAO]
OLHO A SER OPERADO: [OLHO]
TIPO DE LIO: [TIPO_LIO]

____________________________
[NOME_MEDICO]
CRM [CRM] - RQE [RQE]`,
        fields: [
          { key: 'tipo_catarata', label: 'Tipo de Catarata', type: 'select', options: ['Nuclear', 'Cortical', 'Subcapsular posterior', 'Mista'], required: true },
          { key: 'grau', label: 'Grau', type: 'select', options: ['I', 'II', 'III', 'IV', 'V'], required: true },
          { key: 'olho', label: 'Olho a operar', type: 'select', options: ['OD', 'OE', 'AO'], required: true },
          { key: 'tipo_lio', label: 'Tipo de LIO', type: 'select', options: ['Monofocal', 'Multifocal', 'Tórica', 'EDOF'] },
          { key: 'indicacao', label: 'Indicação', type: 'textarea', required: true },
        ],
      },
      {
        seedId: 'consulta-glaucoma',
        type: 'EXAM',
        title: 'Consulta de glaucoma',
        content: `ACOMPANHAMENTO DE GLAUCOMA

Paciente: [NOME_PACIENTE]
Data: [DATA]

1. MEDICAÇÕES EM USO
   [MEDICACOES_GLAUCOMA]

2. ACUIDADE VISUAL
   OD: [AV_OD]
   OE: [AV_OE]

3. TONOMETRIA (Goldmann)
   OD: [PIO_OD] mmHg (Horário: [HORA])
   OE: [PIO_OE] mmHg

4. PAQUIMETRIA
   OD: [PAQUI_OD] μm
   OE: [PAQUI_OE] μm

5. GONIOSCOPIA
   OD: [GONIO_OD]
   OE: [GONIO_OE]

6. FUNDOSCOPIA - DISCO ÓPTICO
   OD: E/D [ED_OD] | Regra ISNT: [ISNT_OD] | Hemorragias: [HEMOR_OD]
   OE: E/D [ED_OE] | Regra ISNT: [ISNT_OE] | Hemorragias: [HEMOR_OE]

7. EXAMES COMPLEMENTARES
   Campo Visual: [CV_RESULTADO]
   OCT CFNR: [OCT_RESULTADO]
   Curva Tensional: [CURVA]

CLASSIFICAÇÃO: [CLASSIFICACAO_GLAUCOMA]
ESTADIAMENTO: [ESTADIAMENTO]
CONDUTA: [CONDUTA]

____________________________
[NOME_MEDICO]
CRM [CRM] - RQE [RQE]`,
        fields: [
          { key: 'pio_od', label: 'PIO OD (mmHg)', type: 'number', required: true },
          { key: 'pio_oe', label: 'PIO OE (mmHg)', type: 'number', required: true },
          { key: 'ed_od', label: 'E/D OD', type: 'text', required: true },
          { key: 'ed_oe', label: 'E/D OE', type: 'text', required: true },
          { key: 'classificacao', label: 'Classificação', type: 'select', options: ['GPAA', 'GPAF', 'Glaucoma Secundário', 'Glaucoma Congênito', 'Suspeito de Glaucoma'], required: true },
          { key: 'estadiamento', label: 'Estadiamento', type: 'select', options: ['Inicial', 'Moderado', 'Avançado'] },
          { key: 'conduta', label: 'Conduta', type: 'textarea', required: true },
        ],
      },
      {
        seedId: 'atestado-comparecimento',
        type: 'CERTIFICATE',
        title: 'Atestado de comparecimento',
        content: `ATESTADO DE COMPARECIMENTO

Atesto, para os devidos fins, que o(a) Sr(a). {{paciente}}, portador(a) do CPF {{cpf}}, esteve presente nesta clínica na data de {{data}}, no período das ___:___ às ___:___, para consulta médica oftalmológica.

{{data}}.


____________________________________
{{medico}}
CRM: {{crm}}`,
      },
      {
        seedId: 'atestado-afastamento',
        type: 'CERTIFICATE',
        title: 'Atestado de afastamento (repouso)',
        content: `ATESTADO MÉDICO

Atesto, para os devidos fins, que o(a) Sr(a). {{paciente}}, portador(a) do CPF {{cpf}}, esteve sob meus cuidados médicos na data de {{data}} e necessita de afastamento de suas atividades habituais por _____ (______________) dias, a contar desta data, por motivo de saúde.

CID-10: ________

{{data}}.


____________________________________
{{medico}}
CRM: {{crm}}`,
      },
      {
        seedId: 'atestado-aptidao',
        type: 'CERTIFICATE',
        title: 'Atestado de aptidão visual',
        content: `ATESTADO DE APTIDÃO VISUAL

Atesto, para os devidos fins, que o(a) Sr(a). {{paciente}}, portador(a) do CPF {{cpf}}, foi submetido(a) a exame oftalmológico na data de {{data}}, apresentando:

ACUIDADE VISUAL
   OD: ______
   OE: ______
   AO: ______

CAMPO VISUAL: ____________________
SENSO CROMÁTICO: ____________________

PARECER: O(a) paciente encontra-se APTO(A) / INAPTO(A) do ponto de vista oftalmológico para __________________________.

Observações: ________________________________________

{{data}}.


____________________________________
{{medico}}
CRM: {{crm}}`,
      },
      {
        seedId: 'encaminhamento-medico',
        type: 'REFERRAL',
        title: 'Encaminhamento médico',
        content: `ENCAMINHAMENTO MÉDICO

Encaminho o(a) paciente {{paciente}}, portador(a) do CPF {{cpf}}, à(o) especialista em ____________________________ para avaliação e conduta.

MOTIVO DO ENCAMINHAMENTO:
________________________________________________________
________________________________________________________

HIPÓTESE DIAGNÓSTICA:
________________________________________________________

CID-10: ________

EXAMES REALIZADOS / ANEXOS:
________________________________________________________

Agradeço a atenção e fico à disposição para informações complementares.

{{data}}.


____________________________________
{{medico}}
CRM: {{crm}}`,
      },
      {
        seedId: 'declaracao-acompanhante',
        type: 'CERTIFICATE',
        title: 'Declaração de acompanhante',
        content: `DECLARAÇÃO DE ACOMPANHANTE

Declaro, para os devidos fins, que o(a) Sr(a). {{acompanhante}}, portador(a) do CPF {{acompanhante_cpf}}, acompanhou o(a) paciente {{paciente}}, CPF {{cpf}}, em consulta médica oftalmológica nesta clínica na data de {{data}}, no período das ___:___ às ___:___.

A presença do(a) acompanhante foi necessária em razão de _________________________________________.

{{data}}.


____________________________________
{{medico}}
CRM: {{crm}}`,
      },
      {
        seedId: 'laudo-oftalmologico-simples',
        type: 'CERTIFICATE',
        title: 'Laudo oftalmológico simples',
        content: `LAUDO OFTALMOLÓGICO

Paciente: {{paciente}}
CPF: {{cpf}}
Data do exame: {{data}}

ANAMNESE:
________________________________________________________

ACUIDADE VISUAL
   OD: ______ (sc)   ______ (cc)
   OE: ______ (sc)   ______ (cc)

REFRAÇÃO
   OD: ______________________
   OE: ______________________

BIOMICROSCOPIA
   OD: ______________________
   OE: ______________________

TONOMETRIA (Goldmann)
   OD: ______ mmHg
   OE: ______ mmHg

FUNDOSCOPIA
   OD: ______________________
   OE: ______________________

DIAGNÓSTICO (CID-10): ________
CONDUTA: ________________________________________________

{{data}}.


____________________________________
{{medico}}
CRM: {{crm}}`,
      },
      // ─── Laudos com tokens de exame ({{ex:...}}) ───────────────────
      // Modelos CERTIFICATE cujo corpo puxa valores MEDIDOS do painel
      // "Exames do atendimento". Os tokens {{ex:<modulo>}} são resolvidos
      // (assados) ao carregar o modelo / clicar em "Preencher exames" —
      // ver src/utils/examTags.ts. `examModuleIds` documenta quais módulos
      // o laudo espera (e pode pré-selecionar o tipo de atendimento).
      ...EXAM_LAUDO_SEEDS,
      {
        seedId: 'retorno-pos-operatorio',
        type: 'EXAM',
        title: 'Retorno pós-operatório',
        content: `RETORNO PÓS-OPERATÓRIO

Paciente: [NOME_PACIENTE]
Data: [DATA]
Cirurgia realizada: [CIRURGIA]
Data da cirurgia: [DATA_CIRURGIA]
DPO: [DPO] dias

1. QUEIXAS PÓS-OPERATÓRIAS
   [QUEIXAS_POS]

2. ACUIDADE VISUAL
   OD: [AV_OD] (sc)
   OE: [AV_OE] (sc)

3. BIOMICROSCOPIA
   Olho operado ([OLHO]):
   - Pálpebras: [PALPEBRAS]
   - Conjuntiva: [CONJUNTIVA]
   - Córnea: [CORNEA]
   - Câmara anterior: [CA]
   - LIO/Cristalino: [LIO_STATUS]
   - Seidel: [SEIDEL]

4. TONOMETRIA
   OD: [PIO_OD] mmHg
   OE: [PIO_OE] mmHg

5. FUNDOSCOPIA (se possível)
   [FUNDOSCOPIA_POS]

EVOLUÇÃO: [EVOLUCAO]
CONDUTA: [CONDUTA_POS]
MEDICAÇÕES: [MEDICACOES_POS]
RETORNO: [RETORNO]

____________________________
[NOME_MEDICO]
CRM [CRM] - RQE [RQE]`,
        fields: [
          { key: 'cirurgia', label: 'Cirurgia Realizada', type: 'select', options: ['Facoemulsificação + LIO', 'Trabeculectomia', 'Vitrectomia', 'Retinopexia', 'Pterígio', 'Outro'], required: true },
          { key: 'dpo', label: 'DPO (dias)', type: 'number', required: true },
          { key: 'olho', label: 'Olho Operado', type: 'select', options: ['OD', 'OE'], required: true },
          { key: 'seidel', label: 'Seidel', type: 'select', options: ['Negativo', 'Positivo'], required: true, defaultValue: 'Negativo' },
          { key: 'evolucao', label: 'Evolução', type: 'textarea', required: true },
          { key: 'conduta', label: 'Conduta', type: 'textarea', required: true },
        ],
      },
      // ─── Email ao fornecedor (pedido de lente de contato) ───────────
      // Template inicial modelando o fluxo "pedido de crédito (saldo)"
      // que o médico envia pra CooperVision. Placeholders específicos
      // de pedido de lente (ver src/utils/lc/emailTemplate.ts):
      //   {{paciente}} {{medico}} {{data}} {{familia}} {{od}} {{oe}}
      //   {{od.ean}} {{oe.ean}} {{od.detalhes}} {{oe.detalhes}}
      // Os campos emailTo/emailCc/emailSubject também passam pelo
      // mesmo engine de substituição antes de virarem o mailto: link.
      //
      // O corpo usa `{{od.detalhes}}` / `{{oe.detalhes}}` — blocos
      // multi-linha com todas as características da lente (família,
      // descrição, EAN, BC, DIA, SPH, CYL, AXIS, Tipo) — para que o
      // fornecedor confira o pedido sem precisar abrir a planilha.
      {
        seedId: 'pedido-lente-coopervision-credito',
        type: 'LENS_ORDER_EMAIL',
        title: 'CooperVision — Pedido de crédito (saldo)',
        emailTo: 'pedido.brasil@coopervision.com',
        emailCc: 't-ifiusa@coopervision.com',
        emailSubject: 'Pedido de crédito (saldo) - MALHEIRO E FIGUEIREDO OTICA LTDA - código 211829',
        content: `Pedido de crédito (saldo) - MALHEIRO E FIGUEIREDO OTICA LTDA - código 211829
ATENÇÃO: CASO NÃO HAJA SALDO, PODE ENVIAR O PEDIDO E A FATURA.
email enviado para
pedido.brasil@coopervision.com
copiado para t-ifiusa@coopervision.com (apenas para monitoramento, em caso de problemas com o envio)

Paciente {{paciente}}


Pedido de {{familia}}

OD:
{{od.detalhes}}

OE:
{{oe.detalhes}}`,
      },
    ];

    for (const template of defaults) {
      const { seedId, ...templateData } = template;
      const docRef = doc(db, 'clinics', clinicId, 'templates', seedId);
      await setDoc(docRef, templateData, { merge: true });
    }
  },

  /**
   * Migration idempotente: o template seed de "Declaração de acompanhante"
   * usava `____________________________________` como espaço pra preencher
   * o nome do acompanhante manualmente. Agora usamos os placeholders
   * `{{acompanhante}}` e `{{acompanhante_cpf}}` pra preencher automaticamente
   * a partir do campo "Acompanhante do dia" na aba Entradas.
   *
   * Esta função substitui APENAS quando o template ainda tem o conteúdo
   * antigo — se o médico personalizou, deixa quieto. Chamada pelo
   * ClinicProvider uma vez por sessão. Não bloqueia se falhar.
   */
  async upgradeAccompanistTemplate(clinicId: string): Promise<void> {
    const ref = doc(db, 'clinics', clinicId, 'templates', 'declaracao-acompanhante');
    const snap = await getDoc(ref);
    if (!snap.exists()) return;
    const data = snap.data() as ClinicalTemplate;
    if (!data.content) return;
    const oldFragment = 'o(a) Sr(a). ____________________________________, portador(a) do CPF ___.___.___-__,';
    if (!data.content.includes(oldFragment)) return;
    const newFragment = 'o(a) Sr(a). {{acompanhante}}, portador(a) do CPF {{acompanhante_cpf}},';
    await updateDoc(ref, { content: data.content.replace(oldFragment, newFragment) });
  },

  /**
   * Backfill dos modelos de laudo com tokens de exame (`EXAM_LAUDO_SEEDS`).
   * Clínicas que já existiam antes desta feature nunca passam pelo path
   * "zero templates" do `seedDefaultTemplates`, então os laudos novos não
   * apareceriam. Esta função planta APENAS os que faltam (setDoc merge por
   * seedId) — não toca em nenhum outro template nem reverte edições do
   * médico. Idempotente; chamada pelo ClinicProvider uma vez por sessão.
   * Devolve `true` se inseriu algo.
   */
  async seedExamLaudosIfMissing(clinicId: string): Promise<boolean> {
    const existing = await this.getTemplates(clinicId);
    const existingIds = new Set(existing.map((t) => t.id));
    const missing = EXAM_LAUDO_SEEDS.filter((s) => !existingIds.has(s.seedId));
    if (missing.length === 0) return false;
    for (const { seedId, ...data } of missing) {
      const ref = doc(db, 'clinics', clinicId, 'templates', seedId);
      await setDoc(ref, data, { merge: true });
    }
    return true;
  },

  /**
   * Backfill cirúrgico: se a clínica não tiver NENHUM template do tipo
   * `LENS_ORDER_EMAIL`, insere o seed do CooperVision. Usado pelo modal
   * de envio ao fornecedor pra cobrir clínicas criadas ANTES da feature
   * existir (essas clínicas passaram pelo `seedDefaultTemplates` na
   * primeira vez que o médico abriu o app, mas nessa época o tipo
   * `LENS_ORDER_EMAIL` ainda não existia — então o template ficou
   * faltando e só o path "clínica nova, zero templates" do
   * `seedDefaultTemplates` consegue plantá-lo).
   *
   * Idempotente: não sobrescreve nenhum outro template nem altera o
   * que já existe com o mesmo `seedId`.
   *
   * Devolve `true` se inseriu algo, `false` se já havia template de
   * email — o modal usa isso pra decidir se precisa re-buscar a lista.
   */
  async seedLensOrderEmailIfMissing(clinicId: string): Promise<boolean> {
    const existing = await this.getTemplates(clinicId);
    if (existing.some((t) => t.type === 'LENS_ORDER_EMAIL')) return false;
    const docRef = doc(
      db,
      'clinics',
      clinicId,
      'templates',
      'pedido-lente-coopervision-credito',
    );
    await setDoc(
      docRef,
      {
        type: 'LENS_ORDER_EMAIL',
        title: 'CooperVision — Pedido de crédito (saldo)',
        emailTo: 'pedido.brasil@coopervision.com',
        emailCc: 't-ifiusa@coopervision.com',
        emailSubject:
          'Pedido de crédito (saldo) - MALHEIRO E FIGUEIREDO OTICA LTDA - código 211829',
        content: `Pedido de crédito (saldo) - MALHEIRO E FIGUEIREDO OTICA LTDA - código 211829
ATENÇÃO: CASO NÃO HAJA SALDO, PODE ENVIAR O PEDIDO E A FATURA.
email enviado para
pedido.brasil@coopervision.com
copiado para t-ifiusa@coopervision.com (apenas para monitoramento, em caso de problemas com o envio)

Paciente {{paciente}}


Pedido de {{familia}}

OD:
{{od.detalhes}}

OE:
{{oe.detalhes}}`,
      },
      { merge: true },
    );
    return true;
  },
};
